/**
 * `PerformanceTimeSeriesChart` — Task 157.
 *
 * A small, dependency-free SVG line/area chart for one campaign's daily
 * performance series. No charting library (recharts etc.) — the scale/path
 * math lives in the tested-pure `chartGeometry` module; this file is the
 * thin React + SVG shell around it.
 *
 * - Metric selector (spend, impressions, clicks, conversions, ROAS, CTR),
 *   Arabic labels, owned as local state.
 * - Responsive via `viewBox` + `width:100%` (no ResizeObserver needed).
 * - Accessible: `<title>` + `<desc>` describe the active metric + range.
 * - Time flows LEFT (oldest) → RIGHT (newest) inside the SVG — the chart's
 *   internal axis stays LTR while the surrounding container is RTL.
 * - Null metric days (CTR / ROAS can be null) render as GAPS: a null day is
 *   simply not plotted, so the line connects only real data points.
 * - Click a point → its date + value pin under the chart (the readout also
 *   defaults to the latest plotted point).
 */

import { useMemo, useState } from 'react';
import type { PaidMediaPerformanceSeriesPoint } from '../../api/types';
import {
  buildAreaPath,
  buildLinePath,
  computePoints,
  computeScale,
  formatAxisValue,
  niceTicks,
  type ChartDimensions,
} from './chartGeometry';
import { firstProvisionalIndex } from './freshness';

type MetricKey = 'spend' | 'impressions' | 'clicks' | 'conversions' | 'roas' | 'ctr';

// Display kind: counts/money, CTR as a percentage, ROAS as a multiplier.
type MetricKind = 'count' | 'percent' | 'roas';

interface MetricDef {
  readonly key: MetricKey;
  readonly label: string;
  readonly kind: MetricKind;
}

const METRICS: ReadonlyArray<MetricDef> = [
  { key: 'spend', label: 'الإنفاق', kind: 'count' },
  { key: 'impressions', label: 'الظهور', kind: 'count' },
  { key: 'clicks', label: 'النقرات', kind: 'count' },
  { key: 'conversions', label: 'التحويلات', kind: 'count' },
  { key: 'roas', label: 'ROAS', kind: 'roas' },
  { key: 'ctr', label: 'CTR', kind: 'percent' },
];

const VIEW: ChartDimensions = { width: 640, height: 240, padding: 32 };

interface PerformanceTimeSeriesChartProps {
  readonly series: ReadonlyArray<PaidMediaPerformanceSeriesPoint>;
  /**
   * `YYYY-MM-DD` floor (from `freshness.provisionalFromDate`); series points on
   * or after this date are within Meta's mutable last-48h and render as
   * PROVISIONAL (shaded band + dashed markers). Omitted → nothing marked.
   */
  readonly provisionalFromDate?: string;
}

function metricValue(point: PaidMediaPerformanceSeriesPoint, key: MetricKey): number | null {
  const v = point[key];
  return v === null || v === undefined ? null : v;
}

function fmtValue(value: number, kind: MetricKind): string {
  if (kind === 'percent') {
    return `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
  }
  if (kind === 'roas') return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}x`;
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// Y-axis tick label: counts use the compact 1.2K/3.4M form; ratios mirror the
// readout (percentage / multiplier) so the axis stays readable.
function fmtAxisTick(value: number, kind: MetricKind): string {
  if (kind === 'percent') {
    return `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
  }
  if (kind === 'roas') return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}x`;
  return formatAxisValue(value);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('ar-SA');
}

export function PerformanceTimeSeriesChart({
  series,
  provisionalFromDate,
}: PerformanceTimeSeriesChartProps) {
  const [metric, setMetric] = useState<MetricKey>('spend');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const active = METRICS.find((m) => m.key === metric) ?? METRICS[0];

  // Per-day values for the active metric over the WHOLE series (nulls KEPT as
  // gaps — count metrics never null; CTR/ROAS can be null on zero-denominator
  // days). computePoints keeps each surviving point at its ORIGINAL index, so
  // a null day does NOT compact the axis and the line/area break across it
  // instead of bridging the gap (Codex P2).
  const seriesValues = useMemo(
    () => series.map((point) => metricValue(point, metric)),
    [series, metric],
  );
  const scale = useMemo(
    () => computeScale(seriesValues.filter((v): v is number => v !== null)),
    [seriesValues],
  );
  const points = useMemo(() => computePoints(seriesValues, VIEW, scale), [seriesValues, scale]);
  const linePath = useMemo(() => buildLinePath(points), [points]);
  const areaPath = useMemo(() => buildAreaPath(points, VIEW), [points]);
  const ticks = useMemo(() => niceTicks(scale.min, scale.max, 4), [scale]);

  // Readout: the clicked point (matched by its ORIGINAL series index) or the
  // latest plotted (non-null) point.
  const readoutPoint =
    (selectedIndex !== null ? points.find((p) => p.index === selectedIndex) : undefined) ??
    (points.length > 0 ? points[points.length - 1] : undefined);
  const readoutIndex = readoutPoint?.index ?? -1;
  const readout = readoutPoint
    ? { date: series[readoutPoint.index]?.metric_date ?? '', value: readoutPoint.value }
    : null;

  // Task 158 — series dates within Meta's mutable last-48h are provisional
  // (shaded band + dashed markers). The band is anchored on the SERIES date
  // axis (not plotted points), so a null CTR/ROAS day inside the last 48h is
  // still shaded (Codex P2); `series` is date-ascending, so every index from
  // the first provisional one onward is provisional.
  const isProvisional = (seriesIndex: number): boolean =>
    provisionalFromDate != null && (series[seriesIndex]?.metric_date ?? '') >= provisionalFromDate;
  const firstProvIndex = firstProvisionalIndex(
    series.map((pt) => pt.metric_date),
    provisionalFromDate,
  );
  const hasProvisional = firstProvIndex >= 0;
  const provisionalBandX =
    hasProvisional && series.length > 0
      ? series.length === 1
        ? VIEW.padding
        : VIEW.padding + ((VIEW.width - 2 * VIEW.padding) * firstProvIndex) / (series.length - 1)
      : null;
  const readoutProvisional = readoutPoint ? isProvisional(readoutPoint.index) : false;

  const hasData = points.length > 0;
  const innerTop = VIEW.padding;
  const innerBottom = VIEW.height - VIEW.padding;
  const innerLeft = VIEW.padding;
  const innerRight = VIEW.width - VIEW.padding;

  return (
    <div dir="rtl">
      <div className="sec-title" style={{ marginBottom: 10 }}>
        <h2>المخطط الزمني</h2>
        <span className="meta">{active.label}</span>
      </div>

      <div
        role="group"
        aria-label="اختيار المقياس"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}
      >
        {METRICS.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`chip${m.key === metric ? ' on' : ''}`}
            aria-pressed={m.key === metric}
            onClick={() => {
              setMetric(m.key);
              setSelectedIndex(null);
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {!hasData ? (
        <div className="body" style={{ padding: '24px 4px', color: 'var(--muted)' }}>
          لا توجد نقاط بيانات لعرضها لهذا المقياس ضمن النطاق المحدد.
        </div>
      ) : (
        <>
          <svg
            viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
            width="100%"
            height="auto"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-labelledby="perf-chart-title perf-chart-desc"
            style={{ display: 'block', overflow: 'visible' }}
          >
            <title id="perf-chart-title">{`المخطط الزمني — ${active.label}`}</title>
            <desc id="perf-chart-desc">
              {`سلسلة زمنية يومية للمقياس ${active.label}؛ المحور الأفقي يمثل الزمن من الأقدم (يسار) إلى الأحدث (يمين)، ${points.length} نقطة بيانات.`}
            </desc>

            {/* Y gridlines + tick labels (LTR axis numbers). */}
            {ticks.map((t) => {
              const denom = scale.max - scale.min || 1;
              const frac = Math.min(Math.max((t - scale.min) / denom, 0), 1);
              const y = innerTop + (innerBottom - innerTop) * (1 - frac);
              return (
                <g key={`tick-${t}`}>
                  <line
                    x1={innerLeft}
                    x2={innerRight}
                    y1={y}
                    y2={y}
                    stroke="var(--line-2)"
                    strokeWidth={1}
                  />
                  <text
                    x={innerLeft - 6}
                    y={y + 3}
                    textAnchor="end"
                    fontSize={10}
                    fill="var(--muted)"
                  >
                    {fmtAxisTick(t, active.kind)}
                  </text>
                </g>
              );
            })}

            {/* Baseline. */}
            <line
              x1={innerLeft}
              x2={innerRight}
              y1={innerBottom}
              y2={innerBottom}
              stroke="var(--line-3)"
              strokeWidth={1}
            />

            {/* Provisional last-48h band (drawn behind the series). */}
            {provisionalBandX !== null ? (
              <rect
                x={provisionalBandX}
                y={innerTop}
                width={Math.max(innerRight - provisionalBandX, 0)}
                height={Math.max(innerBottom - innerTop, 0)}
                fill="var(--muted)"
                opacity={0.1}
              />
            ) : null}

            {areaPath ? <path d={areaPath} fill="var(--tangerine-soft)" opacity={0.5} /> : null}
            {linePath ? (
              <path
                d={linePath}
                fill="none"
                stroke="var(--tangerine)"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ) : null}

            {/* Data-point markers (clickable). */}
            {points.map((p) => {
              const isSel = p.index === readoutIndex;
              const prov = isProvisional(p.index);
              return (
                <circle
                  key={`pt-${p.index}`}
                  cx={p.x}
                  cy={p.y}
                  r={isSel ? 4.5 : 3}
                  fill={isSel && !prov ? 'var(--tangerine)' : 'var(--paper)'}
                  stroke="var(--tangerine)"
                  strokeWidth={1.5}
                  strokeDasharray={prov ? '2 2' : undefined}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedIndex(p.index)}
                >
                  <title>{`${fmtDate(series[p.index]?.metric_date ?? '')} · ${fmtValue(
                    p.value,
                    active.kind,
                  )}${prov ? ' · قابلة للتغيير' : ''}`}</title>
                </circle>
              );
            })}
          </svg>

          {readout ? (
            <div className="body" style={{ marginTop: 8 }}>
              <span className="lbl">{active.label}</span>{' '}
              <span className="num">{fmtDate(readout.date)}</span>
              {' — '}
              <span className="num">{fmtValue(readout.value, active.kind)}</span>
              {readoutProvisional ? <span className="meta"> · قابلة للتغيير</span> : null}
            </div>
          ) : null}

          {hasProvisional ? (
            <p className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
              المنطقة المظللة تمثل آخر 48 ساعة، وبياناتها قابلة للتغيير.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
